import { useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { AddressBookEntry, AddressBookEntryInput } from "../models";
import { queryKeys } from "../queryKeys";
import { axiosInstance, validateUuid } from "../../utils";

const showError = (toast: ReturnType<typeof useToast>, error: unknown) => {
    const axiosError = error as AxiosError;
    toast({
        title: "Changes not saved",
        description: `[${axiosError.response?.status ?? "ERR"}]: ${axiosError.message}`,
        status: "error",
        isClosable: true,
        duration: null,
    });
};

export function useCreateAddressBookEntry() {
    const axios = axiosInstance();
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation({
        mutationFn: (entry: AddressBookEntryInput) => axios.post<AddressBookEntry>("api/AddressBook", entry).then(response => response.data),
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.addressBook.entries.queryKey);
            toast({ title: "Address book entry created", status: "success", isClosable: true });
        },
        onError: error => showError(toast, error),
    });
}

export function useUpdateAddressBookEntry() {
    const axios = axiosInstance();
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation({
        mutationFn: ({ id, entry }: { id: string; entry: AddressBookEntryInput }) =>
            axios.put<AddressBookEntry>(`api/AddressBook/${validateUuid(id)}`, entry).then(response => response.data),
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.addressBook.entries.queryKey);
            toast({ title: "Address book entry updated", status: "success", isClosable: true });
        },
        onError: error => showError(toast, error),
    });
}

export function useDeleteAddressBookEntry() {
    const axios = axiosInstance();
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation({
        mutationFn: (id: string) => axios.delete(`api/AddressBook/${validateUuid(id)}`),
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.addressBook.entries.queryKey);
            toast({ title: "Address book entry deleted", status: "success", isClosable: true });
        },
        onError: error => showError(toast, error),
    });
}
